import { useCallback, useEffect, useRef, useState } from 'react';

const PULL_THRESHOLD = 72;
const MAX_PULL = 120;

interface UsePullToRefreshOptions {
  enabled: boolean;
  onRefresh: () => Promise<void>;
  scrollRef: React.RefObject<HTMLElement | null>;
}

export function usePullToRefresh({
  enabled,
  onRefresh,
  scrollRef,
}: UsePullToRefreshOptions): {
  pullDistance: number;
  isRefreshing: boolean;
  isPulling: boolean;
} {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const pullDistanceRef = useRef(0);
  const pulling = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const resetPull = useCallback(() => {
    pulling.current = false;
    pullDistanceRef.current = 0;
    setPullDistance(0);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const el = scrollRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if (isRefreshing || el.scrollTop > 0) return;
      touchStartY.current = e.touches[0].clientY;
      pulling.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pulling.current || isRefreshing) return;
      const delta = e.touches[0].clientY - touchStartY.current;
      if (delta <= 0 || el.scrollTop > 0) {
        resetPull();
        return;
      }
      e.preventDefault();
      const next = Math.min(delta * 0.45, MAX_PULL);
      pullDistanceRef.current = next;
      setPullDistance(next);
    };

    const onTouchEnd = async () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (pullDistanceRef.current >= PULL_THRESHOLD && !isRefreshing) {
        setIsRefreshing(true);
        setPullDistance(PULL_THRESHOLD);
        try {
          await onRefreshRef.current();
        } finally {
          setIsRefreshing(false);
          pullDistanceRef.current = 0;
          setPullDistance(0);
        }
      } else {
        resetPull();
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enabled, scrollRef, isRefreshing, resetPull]);

  return {
    pullDistance,
    isRefreshing,
    isPulling: pullDistance > 0 || isRefreshing,
  };
}
